import { useEffect, useState } from 'react'
import { Download, Loader2, Trophy } from 'lucide-react'
import * as XLSX from 'xlsx'
import api from '../../utils/api'

const PAGE_SIZE = 100

// 时间格式化：YYMMDD（如 2026-07-29 -> 260729）
function formatYYMMDD(value) {
  if (!value) return '-'
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return '-'
  return `${m[1].slice(2)}${m[2]}${m[3]}`
}

// 榜单卡片（标题 + 表格）
function RankingCard({ title, ranking }) {
  const rows = ranking?.list || []
  const total = ranking?.total || 0

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">共 {total} 人</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">排名</th>
              <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UID</th>
              <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">手机号</th>
              <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">充值金额</th>
              <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">充值次数</th>
              <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">充值时间</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {rows.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-3 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">暂无数据</td>
              </tr>
            ) : (
              rows.map((item) => (
                <tr key={item.uid} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {item.rank <= 3 ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white bg-orange-400">
                        {item.rank}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-600 dark:text-gray-300">{item.rank}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{item.uid}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{item.phone || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-white">
                    {Number(item.total_amount).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-sm text-gray-600 dark:text-gray-300">
                    {item.recharge_count ?? 0}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    {formatYYMMDD(item.last_recharged_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function RechargeRankingTab() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [jumpPage, setJumpPage] = useState('')

  // 导出全量 xlsx（三个榜单各一个 sheet，与当前标签页无关）
  const handleExport = async () => {
    setExporting(true)
    try {
      const response = await api.get('/admin/recharge-ranking/export', { timeout: 0 })
      const payload = response?.data?.data || response?.data
      const HEADERS = ['排名', 'UID', '手机号', '充值金额', '充值次数', '最近充值时间']
      const buildSheetRows = (list) => [
        HEADERS,
        ...(Array.isArray(list) ? list : []).map((item) => [
          item.rank,
          item.uid,
          item.phone ?? '',
          Number(Number(item.total_amount || 0).toFixed(2)),
          item.recharge_count ?? 0,
          item.last_recharged_at || ''
        ])
      ]
      const workbook = XLSX.utils.book_new()
      const sheets = [
        { name: '充值总排名', rows: buildSheetRows(payload?.total_ranking) },
        { name: '近30天充值排名', rows: buildSheetRows(payload?.last30_ranking) },
        { name: '近14天充值排名', rows: buildSheetRows(payload?.last14_ranking) }
      ]
      sheets.forEach(({ name, rows }) => {
        const worksheet = XLSX.utils.aoa_to_sheet(rows)
        worksheet['!cols'] = [{ wch: 6 }, { wch: 10 }, { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 20 }]
        XLSX.utils.book_append_sheet(workbook, worksheet, name)
      })
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      XLSX.writeFile(workbook, `充值排行全量_${dateStr}.xlsx`)
    } catch (err) {
      console.error('导出充值排行失败:', err)
      alert('导出失败，请稍后重试')
    } finally {
      setExporting(false)
    }
  }

  const fetchData = async (targetPage) => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/admin/recharge-ranking', {
        params: { page: targetPage, limit: PAGE_SIZE }
      })
      // 响应拦截器会把统一包装 {status, message, data} 解包平铺，故兼容两种结构
      if (response?.data?.success) {
        setData(response.data.data || response.data)
        setPage(targetPage)
      } else {
        setError('接口返回异常')
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || '获取充值排行失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(1)
  }, [])

  const handleJump = () => {
    const pageNum = Number(jumpPage)
    if (!jumpPage.trim() || isNaN(pageNum)) {
      setJumpPage('')
      return
    }
    if (pageNum < 1 || pageNum > maxPages) {
      alert(`请输入 1 ~ ${maxPages} 之间的页码`)
      return
    }
    fetchData(pageNum)
    setJumpPage('')
  }

  const totalRanking = data?.total_ranking || {}
  const maxPages = Math.max(
    totalRanking.totalPages || 1,
    data?.last30_ranking?.totalPages || 1,
    data?.last14_ranking?.totalPages || 1
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-orange-500" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">充值排行</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchData(page)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50"
          >
            <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/50 disabled:opacity-50"
          >
            <Loader2 className={`w-4 h-4 ${exporting ? 'animate-spin' : 'hidden'}`} />
            <Download className={`w-4 h-4 ${exporting ? 'hidden' : ''}`} />
            {exporting ? '导出中…' : '导出 Excel'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <RankingCard title="充值总排名" ranking={data?.total_ranking} />
        <RankingCard title="近30天充值排名" ranking={data?.last30_ranking} />
        <RankingCard title="近14天充值排名" ranking={data?.last14_ranking} />
      </div>

      <div className="flex items-center justify-center gap-4 py-2">
        <input
          type="number"
          value={jumpPage}
          onChange={(e) => setJumpPage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleJump()}
          placeholder="页码"
          min={1}
          max={maxPages}
          className="w-16 rounded-lg border border-gray-200 dark:border-gray-700 px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        />
        <button
          onClick={handleJump}
          className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          跳转
        </button>
        <button
          onClick={() => fetchData(page - 1)}
          disabled={page === 1 || loading}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          上一页
        </button>
        <span className="text-sm text-gray-600 dark:text-gray-300">
          第 {page} / {maxPages} 页
        </span>
        <button
          onClick={() => fetchData(page + 1)}
          disabled={page >= maxPages || loading}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          下一页
        </button>
      </div>
    </div>
  )
}
